import {
  LucideChartColumn,
  LucideChartColumnBig,
  LucideChartColumnDecreasing,
  LucideChartColumnIncreasing,
  LucideFiles,
  LucideLayoutList,
  LucideTable2,
} from 'lucide-react'
import { useMemo } from 'react'
import { useControlField, useFormContext } from 'remix-validated-form'
import { aOrganization, useAtomValue } from '~/atoms'
import { CheckboxField, InfoTooltip } from '~/components'
import { PdfNotesField, PdfPreparedForField } from '~/components/pdfs'
import { ToggleGroup } from '~/components/ui'
import { ReportPdfType, type Listing, type Report } from '~/models'
import { cn } from '~/utils'

export default function PdfFormContent({
  formId,
  report,
  singleListing = false,
  listings,
}: {
  formId: string
  report?: Report
  singleListing?: boolean
  listings: Listing[]
}) {
  const organization = useAtomValue(aOrganization)
  const formContext = useFormContext(formId)
  const [type, setType] = useControlField<string>('options.type', formId)
  const [showTitlePage] = useControlField<boolean>(
    'options.showTitlePage',
    formId
  )

  const isComparable = useMemo(() => {
    return (
      type === ReportPdfType.saleComp ||
      type === ReportPdfType.leaseComp ||
      type === ReportPdfType.soldComps ||
      type === ReportPdfType.leasedComps
    )
  }, [type])

  const availableComparableTypes = useMemo(() => {
    const keyLabels: { key: string; label: string; icon: React.ReactNode }[] =
      []
    if (listings.some((l) => l.type === 'sale' || l.type === 'saleLease')) {
      keyLabels.push({
        key: 'saleComp',
        label: 'Sale Comps',
        icon: <LucideChartColumnIncreasing className="h-4 w-4" />,
      })
    }
    if (listings.some((l) => l.type === 'lease' || l.type === 'saleLease')) {
      keyLabels.push({
        key: 'leaseComp',
        label: 'Lease Comps',
        icon: <LucideChartColumnDecreasing className="h-4 w-4" />,
      })
    }
    if (listings.some((l) => l.transactions?.some((t) => t.sale))) {
      keyLabels.push({
        key: 'soldComps',
        label: 'Sold Comps',
        icon: <LucideChartColumn className="h-4 w-4" />,
      })
    }
    if (listings.some((l) => l.transactions?.some((t) => t.lease))) {
      keyLabels.push({
        key: 'leasedComps',
        label: 'Leased Comps',
        icon: <LucideChartColumnBig className="h-4 w-4" />,
      })
    }

    return keyLabels
  }, [listings])

  return (
    <>
      <p className="col-span-12 text-sm italic">
        Note: Form changes in preview may take longer to refresh depending on
        the number of listings
      </p>
      <div className={cn('col-span-12', singleListing && 'hidden')}>
        <p className="mb-3 font-medium">
          Report Type
          <InfoTooltip className="ml-2">
            <strong>Flyer PDF</strong>
            <p className="mb-2">
              One listing per page with options to include additional elements
            </p>
            <strong>Summary PDF</strong>
            <p className="mb-2">
              Multiple listings per page with only listing attributes
            </p>
            <strong>Comparison PDF</strong>
            <p className="mb-2">Compare four listings side by side per page</p>
            <strong>Sale Comp PDF</strong>
            <p className="mb-2">
              Analysis of sale listing comparables with pricing statistics
            </p>
            <strong>Lease Comp PDF</strong>
            <p>Analysis of lease space comparables with rate statistics</p>
          </InfoTooltip>
        </p>
        <input type="hidden" name="options.type" value={type} />
        <ToggleGroup
          type="single"
          variant="outline"
          className="flex-wrap justify-start gap-2"
          value={type}
          onValueChange={(type) => {
            if (type) setType(type)
          }}
          disabled={formContext.isSubmitting}>
          <ToggleGroup.Item
            value="flyers"
            className="flex text-center text-foreground">
            <LucideFiles className="h-4 w-4" />
            <p className="px-2 text-foreground">Flyers</p>
          </ToggleGroup.Item>
          <ToggleGroup.Item value="summary">
            <LucideLayoutList className="h-4 w-4" />
            <p className="px-2 text-foreground">Summary</p>
          </ToggleGroup.Item>
          <ToggleGroup.Item value="comparison">
            <LucideTable2 className="h-4 w-4" />
            <p className="px-2 text-foreground">Comparison</p>
          </ToggleGroup.Item>

          {availableComparableTypes.map(({ key, label, icon }) => {
            return (
              <ToggleGroup.Item key={key} value={key}>
                {icon}
                <p className="px-2 text-foreground">{label}</p>
              </ToggleGroup.Item>
            )
          })}
        </ToggleGroup>
      </div>
      <p className="col-span-12 -mb-2 mt-1 font-medium">
        Title Page
        <InfoTooltip className="ml-2">
          <p>
            Introductory page to PDF Report with space for recipient, notes,
            user profile and table of contents
          </p>
        </InfoTooltip>
      </p>
      <CheckboxField
        className="col-span-12"
        label="Show Title Page"
        name="options.showTitlePage"
        disabled={formContext.isSubmitting}
      />
      {showTitlePage && (
        <>
          <CheckboxField
            className="col-span-12"
            label="Show User Profile"
            name="options.showBrokerProfile"
            disabled={formContext.isSubmitting}
          />
          {!isComparable && (
            <CheckboxField
              className={cn('col-span-12', singleListing && 'hidden')}
              label="Show Table of Contents"
              name="options.showContents"
              disabled={formContext.isSubmitting}
            />
          )}
          <PdfPreparedForField
            formId={formId}
            name="options.preparedFor"
            formContext={formContext}
            initialValue={report?.pdf?.options?.preparedFor}
          />
          <PdfNotesField
            formId={formId}
            name="options.notes"
            formContext={formContext}
            initialValue={
              report?.pdf?.options?.notes || report?.description
                ? (report.pdf?.options?.notes || report.description || '')
                    .split('</p>')
                    .map((v) => v.replace('<p>', ''))
                    .join('\n\n')
                    .trim()
                : ''
            }
          />
        </>
      )}
      {!isComparable && (
        <>
          <p className="col-span-12 -mb-2 mt-1 font-medium">
            Map Page
            <InfoTooltip className="ml-2">
              <p>Include numbered listing markers of all listings included</p>
            </InfoTooltip>
          </p>
          <CheckboxField
            className="col-span-12 col-start-1"
            label="Show Map Page"
            name="options.showMapPage"
            disabled={formContext.isSubmitting}
          />
        </>
      )}
      {type === 'flyers' && (
        <>
          <p className="col-span-12 -mb-2 mt-1 font-medium">
            Listings
            <InfoTooltip className="ml-2">
              <p>
                Optionally show additional listing information on separate pages
              </p>
            </InfoTooltip>
          </p>
          <CheckboxField
            className="col-span-12"
            label="Show Brokers on Listings"
            name="options.showBrokers"
            disabled={formContext.isSubmitting}
          />
          <CheckboxField
            className="col-span-12"
            label="Show Separate Lease Spaces"
            name="options.showLeaseSpaces"
            disabled={formContext.isSubmitting}
          />
          <CheckboxField
            className="col-span-12"
            label="Show Separate Lease Transactions"
            name="options.showLeaseTransactions"
            disabled={formContext.isSubmitting}
          />
          <CheckboxField
            className="col-span-12"
            label="Show Additional Listing Images"
            name="options.showImages"
            disabled={formContext.isSubmitting}
          />
          {organization!.options.parcelDataEnabled && (
            <>
              <CheckboxField
                className="col-span-12"
                label="Show Parcel Data"
                name="options.showParcels"
                disabled={formContext.isSubmitting || listings.length > 10}
              />
              {listings.length > 10 && (
                <p className="col-span-12 -mt-4 text-sm italic">
                  Select 10 or fewer listings to add parcel data pages.
                </p>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
